import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsAPI } from '../services/api';
import {
  HiOutlineRefresh,
  HiOutlineVideoCamera,
  HiOutlineCollection,
  HiOutlineUsers,
  HiOutlineFilm,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

function MobileAdminSeries() {
  const { t } = useTranslation();
  const [keyStatus, setKeyStatus] = useState({ configured: false });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncingCurrent, setSyncingCurrent] = useState(0);
  const [syncingTotal, setSyncingTotal] = useState(0);
  const [syncingTitle, setSyncingTitle] = useState('');
  const [eventSource, setEventSource] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [keyRes, statsRes] = await Promise.all([
        settingsAPI.getTMDBKeyStatus(),
        settingsAPI.getStats(),
      ]);
      setKeyStatus(keyRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTVShows = async () => {
    if (!keyStatus.configured) {
      toast.error(t('dashboard.configureApiKey'));
      return;
    }

    setSyncing(true);
    setSyncProgress(0);
    setSyncingCurrent(0);
    setSyncingTotal(0);
    setSyncingTitle(t('common.initializing'));

    try {
      const connection = settingsAPI.syncTVShowsStream({
        onProgress: (data) => {
          setSyncProgress(data.progress);
          setSyncingCurrent(data.current);
          setSyncingTotal(data.total);
          setSyncingTitle(data.title || t('common.initializing'));
        },
        onComplete: (data) => {
          setSyncing(false);
          setSyncProgress(100);
          if (data.success > 0) {
            toast.success(t('settings.syncComplete'));
          }
          if (data.failed > 0) {
            toast.error(t('settings.syncError'));
          }
          loadData();
        },
        onError: (data) => {
          setSyncing(false);
          toast.error(t('settings.syncError'));
        },
      });

      setEventSource(connection);
    } catch (error) {
      console.error('Error starting sync:', error);
      setSyncing(false);
      toast.error(t('settings.syncError'));
    }
  };

  const cancelSync = () => {
    if (eventSource) {
      eventSource.close();
    }
    setSyncing(false);
    toast(t('common.cancel'));
  };

  if (loading) {
    return (
      <div className="mobile-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="mobile-admin-series" data-scroll-lock>
      {/* Stats Cards */}
      <div className="mobile-admin-series__stats">
        <div className="mobile-admin-series__stat-card">
          <div className="mobile-admin-series__stat-icon mobile-admin-series__stat-icon--purple">
            <HiOutlineUsers size={20} />
          </div>
          <div className="mobile-admin-series__stat-info">
            <h3>{stats?.total_users || 0}</h3>
            <p>{t('settings.users')}</p>
          </div>
        </div>
        <div className="mobile-admin-series__stat-card">
          <div className="mobile-admin-series__stat-icon mobile-admin-series__stat-icon--teal">
            <HiOutlineFilm size={20} />
          </div>
          <div className="mobile-admin-series__stat-info">
            <h3>{stats?.total_movies || 0}</h3>
            <p>{t('settings.movies')}</p>
          </div>
        </div>
        <div className="mobile-admin-series__stat-card">
          <div className="mobile-admin-series__stat-icon mobile-admin-series__stat-icon--pink">
            <HiOutlineVideoCamera size={20} />
          </div>
          <div className="mobile-admin-series__stat-info">
            <h3>{stats?.total_tv_shows || 0}</h3>
            <p>{t('settings.series')}</p>
          </div>
        </div>
        <div className="mobile-admin-series__stat-card">
          <div className="mobile-admin-series__stat-icon mobile-admin-series__stat-icon--purple">
            <HiOutlineCollection size={20} />
          </div>
          <div className="mobile-admin-series__stat-info">
            <h3>{stats?.total_watchlist_items || 0}</h3>
            <p>{t('settings.titles')}</p>
          </div>
        </div>
      </div>

      {/* Sync Section */}
      <div className="mobile-admin-series__sync-section">
        <h2>{t('settings.syncTVShows')}</h2>
        <p>{t('settings.syncDescription')}</p>
        
        <button
          className="mobile-admin-series__sync-btn"
          onClick={handleSyncTVShows}
          disabled={syncing || !keyStatus.configured}
        >
          <HiOutlineRefresh size={20} className={syncing ? 'spinning' : ''} />
          <span>{syncing ? t('settings.syncing') : t('settings.syncTVShows')}</span>
        </button>
        
        {!keyStatus.configured && (
          <p className="mobile-admin-series__warning">
            {t('dashboard.configureApiKey')}
          </p>
        )}
      </div>

      {/* Info Section */}
      <div className="mobile-admin-series__info">
        <h3>{t('settings.tmdb')}</h3>
        <p>{t('settings.syncDescription')}</p>
      </div>

      {/* Sync Modal */}
      {syncing && (
        <div className="mobile-sync-modal">
          <div className="mobile-sync-modal__overlay" onClick={cancelSync} />
          <div className="mobile-sync-modal__content">
            <div className="mobile-sync-modal__icon">
              <HiOutlineRefresh size={32} className="spinning" />
            </div>
            <h3 className="mobile-sync-modal__title">{t('settings.syncing')}</h3>
            <p className="mobile-sync-modal__series">{syncingTitle}</p>
            
            <div className="mobile-sync-modal__progress-bar">
              <div 
                className="mobile-sync-modal__progress-fill"
                style={{ width: `${syncProgress}%` }}
              />
            </div>
            
            <div className="mobile-sync-modal__stats">
              <span>{syncingCurrent}/{syncingTotal}</span>
              <span>{syncProgress}%</span>
            </div>
            
            <button 
              className="mobile-sync-modal__cancel"
              onClick={cancelSync}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Bottom spacing */}
      <div className="mobile-admin-series__spacer" />
    </div>
  );
}

export default MobileAdminSeries;
